import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, FileText, Truck, MessageSquare, AlertTriangle, MapPin, Settings, LogOut, Plus, Send, ChevronRight, Clock, CheckCircle, XCircle, Eye, Download, FileCode, Bell, X, HandCoins, ArrowRightLeft, ShoppingCart, Shield, ShieldCheck, ShieldX } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useCart } from "../context/CartContext";
import api from "../lib/api";
import { toast } from "sonner";

const TAB_SECTIONS = [
    { label: "Nakupy", tabs: [
        { id: "orders", label: "Objednavky", icon: Package },
        { id: "invoices", label: "Faktury", icon: FileText },
        { id: "shipping", label: "Expedice", icon: Truck },
    ]},
    { label: "Servis", tabs: [
        { id: "warranties", label: "Zaruky", icon: Shield },
        { id: "complaints", label: "Reklamace", icon: AlertTriangle },
    ]},
    { label: "Sluzby", tabs: [
        { id: "watchdog", label: "Hlidaci pes", icon: Bell },
        { id: "better-price", label: "Cenove nabidky", icon: HandCoins },
        { id: "inquiries", label: "Poptavky", icon: FileText },
    ]},
    { label: "Ucet", tabs: [
        { id: "tickets", label: "Komunikace", icon: MessageSquare },
        { id: "addresses", label: "Adresy", icon: MapPin },
        { id: "settings", label: "Nastaveni", icon: Settings },
    ]},
];

const TABS = TAB_SECTIONS.flatMap(s => s.tabs);

const STATUS_MAP = {
    pending: { label: "Čeká na zpracování", color: "bg-yellow-100 text-yellow-700" },
    processing: { label: "Zpracovává se", color: "bg-blue-100 text-blue-700" },
    shipped: { label: "Odesláno", color: "bg-purple-100 text-purple-700" },
    delivered: { label: "Doručeno", color: "bg-green-100 text-green-700" },
    cancelled: { label: "Zrušeno", color: "bg-red-100 text-red-700" },
    open: { label: "Otevřený", color: "bg-yellow-100 text-yellow-700" },
    answered: { label: "Odpovězeno", color: "bg-blue-100 text-blue-700" },
    closed: { label: "Uzavřeno", color: "bg-gray-100 text-gray-500" },
    new: { label: "Nový", color: "bg-yellow-100 text-yellow-700" },
    resolved: { label: "Vyřešeno", color: "bg-green-100 text-green-700" },
};

const fmt = (n) => n?.toLocaleString("cs-CZ") || "0";

export default function CustomerDashboard() {
    const { customer, logout } = useCustomerAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("orders");
    const [salesRep, setSalesRep] = useState(null);
    const [priceTier, setPriceTier] = useState(null);

    useEffect(() => { if (!customer) navigate("/prihlaseni"); }, [customer, navigate]);
    useEffect(() => {
        if (customer?.email) {
            api.get("/customer/sales-rep").then(r => setSalesRep(r.data)).catch(() => {});
            api.get("/customer/price-tier").then(r => setPriceTier(r.data)).catch(() => {});
        }
    }, [customer]);
    if (!customer) return null;

    return (
        <div className="min-h-screen" style={{ backgroundColor: "var(--sg-bg-light)" }}>
            <Navbar />
            <div className="sg-container py-5">
                {/* Header with profile summary */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: "var(--sg-primary)" }}>
                            {(customer.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <h1 className="text-base font-bold leading-tight" style={{ color: "var(--sg-text)" }}>
                                {customer.name}
                            </h1>
                            <p className="text-xs text-gray-500">{customer.email}</p>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn">
                        <LogOut size={13} className="mr-1" /> Odhlasit
                    </Button>
                </div>

                <div className="flex gap-5">
                    {/* Sidebar */}
                    <aside className="hidden md:block w-52 flex-shrink-0">
                        <div className="sticky top-20 space-y-1">
                            {/* Sales Rep card */}
                            {salesRep?.name && (
                                <div className="border p-3 mb-3" style={{ borderColor: "var(--sg-border)", borderRadius: "var(--sg-radius)", background: "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)" }}>
                                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Vas obchodnik</p>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: "var(--sg-primary)" }}>
                                            {(salesRep.name || "?").charAt(0)}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-800 truncate">{salesRep.name}</p>
                                            {salesRep.phone && <p className="text-[10px] text-gray-500">{salesRep.phone}</p>}
                                            {salesRep.email && <a href={`mailto:${salesRep.email}`} className="text-[10px] text-green-600 hover:underline truncate block">{salesRep.email}</a>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Price tier card */}
                            {priceTier?.current_group && (
                                <div className="border p-3 mb-3" style={{ borderColor: "var(--sg-border)", borderRadius: "var(--sg-radius)" }} data-testid="price-tier-card">
                                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Cenova kategorie</p>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: "var(--sg-primary)" }}>
                                            {priceTier.current_group.code}
                                        </span>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800">{priceTier.current_group.name}</p>
                                            {priceTier.current_group.discount_percent > 0 && (
                                                <p className="text-[10px] text-green-600 font-medium">Sleva {priceTier.current_group.discount_percent}%</p>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-[10px] text-gray-500 mb-1.5">Obrat: <span className="font-semibold text-gray-700">{fmt(Math.round(priceTier.total_spent))} Kc</span></p>
                                    {priceTier.next_group && (
                                        <div className="mt-1.5">
                                            <div className="flex justify-between text-[9px] text-gray-400 mb-0.5">
                                                <span>{priceTier.current_group.code}</span>
                                                <span>{priceTier.next_group.code} ({fmt(priceTier.next_group.min_turnover)} Kc)</span>
                                            </div>
                                            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                <div className="h-full rounded-full transition-all" style={{
                                                    width: `${Math.min(100, priceTier.next_group.min_turnover > 0 ? (priceTier.total_spent / priceTier.next_group.min_turnover) * 100 : 0)}%`,
                                                    backgroundColor: "var(--sg-primary)"
                                                }} />
                                            </div>
                                            <p className="text-[9px] text-gray-400 mt-1">Do dalsi urovne: <span className="font-medium">{fmt(Math.round(priceTier.next_group.remaining))} Kc</span></p>
                                        </div>
                                    )}
                                    {!priceTier.next_group && (
                                        <p className="text-[10px] text-green-600 font-medium mt-1">Nejvyssi uroven!</p>
                                    )}
                                </div>
                            )}

                            {/* Grouped navigation */}
                            <div className="border overflow-hidden" style={{ borderColor: "var(--sg-border)", borderRadius: "var(--sg-radius)" }}>
                                {TAB_SECTIONS.map((section, si) => (
                                    <div key={section.label}>
                                        <div className="px-3 py-1.5 bg-gray-50/70 border-b" style={{ borderColor: "var(--sg-border)" }}>
                                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{section.label}</span>
                                        </div>
                                        {section.tabs.map(t => (
                                            <button key={t.id} onClick={() => setTab(t.id)}
                                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors border-b text-left ${tab === t.id ? "text-white font-medium" : "text-gray-600 hover:bg-gray-50 bg-white"}`}
                                                style={tab === t.id ? { backgroundColor: "var(--sg-primary)", borderColor: "var(--sg-primary)" } : { borderColor: "var(--sg-border)" }}
                                                data-testid={`account-tab-${t.id}`}>
                                                <t.icon size={13} /> {t.label}
                                            </button>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </aside>

                    {/* Mobile tabs */}
                    <div className="md:hidden w-full mb-3 flex gap-1 overflow-x-auto pb-2">
                        {TABS.map(t => (
                            <button key={t.id} onClick={() => setTab(t.id)}
                                className={`flex items-center gap-1 px-3 py-2 text-xs font-medium rounded-lg whitespace-nowrap border ${tab === t.id ? "text-white border-transparent" : "text-gray-500 bg-white border-gray-200"}`}
                                style={tab === t.id ? { backgroundColor: "var(--sg-primary)" } : {}}>
                                <t.icon size={12} /> {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                        {tab === "orders" && <OrdersTab />}
                        {tab === "invoices" && <InvoicesTab />}
                        {tab === "shipping" && <ShippingTab />}
                        {tab === "warranties" && <WarrantiesTab />}
                        {tab === "watchdog" && <WatchdogTab />}
                        {tab === "better-price" && <BetterPriceTab />}
                        {tab === "inquiries" && <InquiriesTab />}
                        {tab === "complaints" && <ComplaintsTab />}
                        {tab === "tickets" && <TicketsTab />}
                        {tab === "addresses" && <AddressesTab />}
                        {tab === "settings" && <SettingsTab />}
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}

function Card({ children, className = "", onClick }) {
    return <div className={`bg-white border p-4 ${className}`} style={{ borderColor: "var(--sg-border)", borderRadius: "var(--sg-radius)" }} onClick={onClick}>{children}</div>;
}

function StatusBadge({ status }) {
    const s = STATUS_MAP[status] || { label: status, color: "bg-gray-100 text-gray-500" };
    return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.color}`}>{s.label}</span>;
}

// ─── Orders ────────────────────────────────────────────────────────────────

function WatchdogTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/customer/watchdog").then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const handleRemove = async (id) => {
        try {
            await api.delete(`/customer/watchdog/${id}`);
            setItems(prev => prev.filter(i => i.id !== id));
            toast.success("Hlidani zruseno");
        } catch { toast.error("Chyba"); }
    };

    if (loading) return <div className="p-8 text-center text-sm text-gray-400">Nacitam...</div>;

    const WTYPE = { stock: "Sklad", price: "Cena", both: "Sklad + Cena" };

    return (
        <div data-testid="watchdog-tab">
            <h2 className="text-base font-bold text-gray-900 mb-3">Hlidaci pes</h2>
            <p className="text-xs text-gray-500 mb-4">Produkty, ktere hlidate. Dostanete e-mail pri zmene dostupnosti nebo ceny.</p>
            {items.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-400 bg-gray-50 rounded-xl">
                    <Eye size={24} className="mx-auto mb-2 text-gray-300" />
                    Zadne hlidane produkty
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map(item => (
                        <div key={item.id} className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 p-3 hover:bg-gray-50/50" data-testid={`watchdog-item-${item.id}`}>
                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                                {item.product_image && <img src={item.product_image} alt="" className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <a href={`/produkt/${item.product_slug}`} className="text-sm font-medium text-gray-800 hover:text-green-700 truncate block">{item.product_name}</a>
                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                    <Badge variant="secondary" className={`text-[10px] ${item.watch_type === "price" ? "bg-purple-50 text-purple-700 border-purple-200" : item.watch_type === "both" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                                        <Eye size={10} className="mr-1" /> {WTYPE[item.watch_type] || "Sklad"}
                                    </Badge>
                                    {item.watch_type !== "stock" && item.target_price && (
                                        <span className="text-[10px] text-purple-600 font-medium">Cil: {item.target_price.toLocaleString("cs-CZ")} Kc</span>
                                    )}
                                    {item.current_price && (
                                        <span className="text-[10px] text-gray-500">Aktualni: {item.current_price.toLocaleString("cs-CZ")} Kc</span>
                                    )}
                                    {item.current_stock_status && (
                                        <span className={`text-[10px] font-medium ${item.current_stock_status === "in_stock" ? "text-green-600" : "text-red-500"}`}>
                                            {item.current_stock_status === "in_stock" ? "Skladem" : "Vyprodano"}
                                        </span>
                                    )}
                                    {item.variant_name && <span className="text-[10px] text-gray-400">{item.variant_name}</span>}
                                </div>
                            </div>
                            <button onClick={() => handleRemove(item.id)} className="text-gray-400 hover:text-red-500 p-1 flex-shrink-0" data-testid={`watchdog-remove-${item.id}`}>
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const BP_STATUS = {
    pending: { label: "Čeká na odpověď", color: "bg-amber-100 text-amber-700", icon: Clock },
    approved: { label: "Schváleno", color: "bg-green-100 text-green-700", icon: CheckCircle },
    rejected: { label: "Zamítnuto", color: "bg-red-100 text-red-700", icon: XCircle },
    counter: { label: "Protinabídka", color: "bg-blue-100 text-blue-700", icon: ArrowRightLeft },
    accepted: { label: "Přijato", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
};

function BetterPriceTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [accepting, setAccepting] = useState(null);
    const { addItem: addToCart } = useCart();

    useEffect(() => {
        api.get("/better-price/my").then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const handleAcceptCounter = async (item) => {
        setAccepting(item.id);
        try {
            const { data } = await api.post(`/better-price/accept/${item.id}`);
            addToCart(data.product, 1, data.counter_price);
            setItems(prev => prev.map(it => it.id === item.id ? { ...it, status: "accepted" } : it));
            toast.success(`${item.product_name} přidán do košíku za ${data.counter_price?.toLocaleString("cs-CZ")} Kč`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Chyba při přijímání nabídky");
        } finally { setAccepting(null); }
    };

    if (loading) return <Card><p className="text-sm text-gray-400 text-center py-6">Načítám nabídky...</p></Card>;

    return (
        <div data-testid="better-price-tab">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold" style={{ color: "var(--sg-text)" }}>Moje cenové nabídky</h2>
                <span className="text-xs text-gray-400">{items.length} nabídek</span>
            </div>
            {items.length === 0 ? (
                <Card>
                    <div className="text-center py-8">
                        <HandCoins size={28} className="mx-auto mb-2 text-gray-300" />
                        <p className="text-sm text-gray-400">Zatím žádné cenové nabídky</p>
                        <p className="text-xs text-gray-400 mt-1">Nabídku odešlete z detailu produktu přes tlačítko "Chci lepší cenu"</p>
                    </div>
                </Card>
            ) : (
                <div className="space-y-2.5">
                    {items.map((item, i) => {
                        const st = BP_STATUS[item.status] || BP_STATUS.pending;
                        const StIcon = st.icon;
                        return (
                            <div key={i} className="flex items-start gap-3 bg-white rounded-xl border border-gray-100 p-3.5 hover:bg-gray-50/50 transition-colors"
                                data-testid={`bp-item-${i}`}>
                                {item.product_image ? (
                                    <Link to={`/produkt/${item.product_slug}`} className="flex-shrink-0">
                                        <img src={item.product_image} alt="" className="w-14 h-14 rounded-lg object-contain bg-gray-50" />
                                    </Link>
                                ) : (
                                    <div className="w-14 h-14 rounded-lg bg-gray-100 flex-shrink-0 flex items-center justify-center">
                                        <Package size={18} className="text-gray-300" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Link to={`/produkt/${item.product_slug}`} className="text-sm font-semibold text-gray-800 hover:text-green-700 truncate">
                                            {item.product_name}
                                        </Link>
                                        <Badge className={`text-[10px] shrink-0 ${st.color}`}>
                                            <StIcon size={10} className="mr-0.5" />{st.label}
                                        </Badge>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-1">
                                        <span>Aktuální cena: <strong className="text-gray-700">{fmt(item.current_price)} Kč</strong></span>
                                        <span>Vaše nabídka: <strong className="text-red-600">{fmt(item.offered_price)} Kč</strong></span>
                                        <span className="text-gray-400">(-{Math.round((1 - item.offered_price / item.current_price) * 100)}%)</span>
                                    </div>
                                    {item.status === "counter" && item.counter_price && (
                                        <div className="flex items-center gap-2 mt-2">
                                            <div className="flex items-center gap-1.5 text-xs bg-blue-50 border border-blue-100 rounded-lg px-2.5 py-1.5 flex-1">
                                                <ArrowRightLeft size={12} className="text-blue-600" />
                                                <span className="text-blue-700">Protinabídka: <strong>{fmt(item.counter_price)} Kč</strong></span>
                                            </div>
                                            <Button size="sm"
                                                onClick={() => handleAcceptCounter(item)}
                                                disabled={accepting === item.id}
                                                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white shrink-0 gap-1"
                                                data-testid={`accept-counter-${item.id}`}>
                                                <ShoppingCart size={12} />
                                                {accepting === item.id ? "Přidávám..." : "Přijmout a do košíku"}
                                            </Button>
                                        </div>
                                    )}
                                    {item.status === "accepted" && item.counter_price && (
                                        <div className="flex items-center gap-1.5 text-xs bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-1.5 mt-1.5">
                                            <CheckCircle size={12} className="text-emerald-600" />
                                            <span className="text-emerald-700">Přijato za <strong>{fmt(item.counter_price)} Kč</strong> — přidáno do košíku</span>
                                        </div>
                                    )}
                                    {item.admin_note && (
                                        <p className="text-xs text-gray-500 mt-1 italic">"{item.admin_note}"</p>
                                    )}
                                    {item.message && (
                                        <p className="text-[11px] text-gray-400 mt-1">Vaše zpráva: {item.message}</p>
                                    )}
                                    <p className="text-[11px] text-gray-400 mt-1">{new Date(item.created_at).toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" })}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


function OrdersTab() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        api.get("/customer/orders").then(r => setOrders(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);

    if (loading) return <Card><p className="text-sm text-gray-400 text-center py-6">Načítám objednávky...</p></Card>;

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("sg_customer_token");

    const STEPS = ["pending", "processing", "shipped", "delivered"];
    const STEP_LABELS = { pending: "Přijato", processing: "Zpracování", shipped: "Odesláno", delivered: "Doručeno" };

    return (
        <div className="space-y-3" data-testid="orders-tab">
            <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-bold" style={{ color: "var(--sg-text)" }}>Moje objednávky</h2>
                <span className="text-xs text-gray-400">{orders.length} objednávek</span>
            </div>
            {orders.length === 0 ? (
                <Card>
                    <div className="text-center py-8">
                        <Package size={28} className="mx-auto mb-2 text-gray-300" />
                        <p className="text-sm text-gray-400">Zatím žádné objednávky</p>
                    </div>
                </Card>
            ) : orders.map(o => {
                const expanded = expandedId === o.id;
                const stepIdx = STEPS.indexOf(o.status);
                const isCancelled = o.status === "cancelled";

                return (
                    <Card key={o.id} className="hover:shadow-sm transition-shadow overflow-hidden">
                        <div className="cursor-pointer" onClick={() => setExpandedId(expanded ? null : o.id)} data-testid={`order-row-${o.id}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-sm font-bold" style={{ color: "var(--sg-primary-dark)" }}>{o.order_number}</span>
                                    <StatusBadge status={o.status} />
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-bold text-gray-900">{fmt(o.total)} Kč</span>
                                    <ChevronRight size={14} className={`text-gray-300 transition-transform ${expanded ? "rotate-90" : ""}`} />
                                </div>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                <span>{new Date(o.created_at).toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" })}</span>
                                <span>{(o.items || []).length} položek</span>
                                {o.payment_method && <span className="capitalize">{o.payment_method === "card" ? "Kartou" : o.payment_method === "bank_transfer" ? "Převodem" : o.payment_method === "cod" ? "Dobírka" : o.payment_method}</span>}
                            </div>

                            {/* Progress timeline */}
                            {!isCancelled && stepIdx >= 0 && (
                                <div className="flex items-center gap-1 mt-3">
                                    {STEPS.map((s, i) => (
                                        <div key={s} className="flex items-center flex-1">
                                            <div className={`flex items-center gap-1.5 ${i <= stepIdx ? "" : "opacity-40"}`}>
                                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${
                                                    i < stepIdx ? "bg-green-500 text-white" :
                                                    i === stepIdx ? "bg-green-500 text-white ring-2 ring-green-200" :
                                                    "bg-gray-200 text-gray-500"
                                                }`}>
                                                    {i < stepIdx ? <CheckCircle size={11} /> : i + 1}
                                                </div>
                                                <span className={`text-[10px] font-medium hidden sm:inline ${i <= stepIdx ? "text-green-700" : "text-gray-400"}`}>
                                                    {STEP_LABELS[s]}
                                                </span>
                                            </div>
                                            {i < STEPS.length - 1 && (
                                                <div className={`flex-1 h-0.5 mx-1 rounded ${i < stepIdx ? "bg-green-400" : "bg-gray-200"}`} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {isCancelled && (
                                <div className="mt-2 flex items-center gap-1.5 text-xs text-red-500">
                                    <XCircle size={12} /> Objednávka byla zrušena
                                </div>
                            )}

                            {o.tracking_number && (
                                <a href={`https://www.ppl.cz/detail-zasilky/${o.tracking_number}`} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 mt-2 text-xs text-green-600 hover:text-green-700 font-medium bg-green-50 px-2.5 py-1 rounded-lg"
                                    onClick={e => e.stopPropagation()}>
                                    <Truck size={12} /> Sledovat zásilku: {o.tracking_number}
                                </a>
                            )}
                        </div>

                        {/* Expanded detail */}
                        {expanded && (
                            <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--sg-border)" }}>
                                {/* Items table */}
                                <table className="w-full text-sm mb-3">
                                    <thead>
                                        <tr className="text-xs text-gray-400 border-b" style={{ borderColor: "var(--sg-border)" }}>
                                            <th className="text-left py-1.5 font-medium">Položka</th>
                                            <th className="text-center py-1.5 font-medium w-16">Ks</th>
                                            <th className="text-right py-1.5 font-medium w-24">Cena/ks</th>
                                            <th className="text-right py-1.5 font-medium w-24">Celkem</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(o.items || []).map((item, i) => (
                                            <tr key={i} className="border-b last:border-0" style={{ borderColor: "var(--sg-border)" }}>
                                                <td className="py-2">
                                                    <div className="flex items-center gap-2">
                                                        {item.image && <img src={item.image} alt="" className="w-8 h-8 rounded border object-contain bg-gray-50" />}
                                                        <span className="text-gray-800 font-medium text-xs">{item.name}</span>
                                                    </div>
                                                </td>
                                                <td className="py-2 text-center text-gray-600 text-xs">{item.quantity}</td>
                                                <td className="py-2 text-right text-gray-600 text-xs">{fmt(item.unit_price || item.price)} Kč</td>
                                                <td className="py-2 text-right font-medium text-gray-800 text-xs">{fmt(item.total_price || (item.quantity * (item.unit_price || item.price)))} Kč</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* Summary */}
                                <div className="flex justify-end">
                                    <div className="text-right space-y-0.5 text-xs">
                                        {o.subtotal && <div className="text-gray-500">Mezisoučet: {fmt(o.subtotal)} Kč</div>}
                                        {o.shipping_cost > 0 && <div className="text-gray-500">Doprava: {fmt(o.shipping_cost)} Kč</div>}
                                        {o.cod_fee > 0 && <div className="text-gray-500">Dobírka: {fmt(o.cod_fee)} Kč</div>}
                                        {o.coupon_discount > 0 && <div className="text-green-600">Sleva: -{fmt(o.coupon_discount)} Kč</div>}
                                        <div className="text-sm font-bold text-gray-900 pt-1 border-t" style={{ borderColor: "var(--sg-border)" }}>Celkem: {fmt(o.total)} Kč</div>
                                    </div>
                                </div>

                                {/* Addresses */}
                                {(o.billing_address || o.shipping_address) && (
                                    <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t" style={{ borderColor: "var(--sg-border)" }}>
                                        {o.billing_address && (
                                            <div>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Fakturační adresa</p>
                                                <p className="text-xs text-gray-600">
                                                    {o.billing_address.name}<br />
                                                    {o.billing_address.street}<br />
                                                    {o.billing_address.city} {o.billing_address.zip}
                                                </p>
                                            </div>
                                        )}
                                        {o.shipping_address && (
                                            <div>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Doručovací adresa</p>
                                                <p className="text-xs text-gray-600">
                                                    {o.shipping_address.name}<br />
                                                    {o.shipping_address.street}<br />
                                                    {o.shipping_address.city} {o.shipping_address.zip}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Download links */}
                                <div className="flex gap-2 mt-3 pt-3 border-t" style={{ borderColor: "var(--sg-border)" }}>
                                    <a href={`${backendUrl}/api/customer/orders/${o.id}/order-pdf?token=${token}`} target="_blank" rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-gray-50" style={{ borderColor: "var(--sg-border)", color: "var(--sg-primary)" }}
                                        data-testid={`order-download-pdf-${o.id}`}>
                                        <Download size={12} /> Objednávka PDF
                                    </a>
                                </div>
                            </div>
                        )}
                    </Card>
                );
            })}
        </div>
    );
}

// ─── Invoices ──────────────────────────────────────────────────────────────

function InvoicesTab() {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/customer/invoices").then(r => {
            setInvoices(r.data || []);
        }).catch(() => {
            api.get("/customer/orders").then(r => setInvoices((r.data || []).filter(o => o.status !== "cancelled"))).catch(() => {});
        }).finally(() => setLoading(false));
    }, []);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("sg_customer_token");

    const paid = invoices.filter(i => i.status === "paid");
    const unpaid = invoices.filter(i => i.status !== "paid");

    const INV_STATUS = {
        paid: { label: "Zaplaceno", cls: "bg-green-100 text-green-700", icon: CheckCircle },
        sent: { label: "Čeká na platbu", cls: "bg-amber-100 text-amber-700", icon: Clock },
        draft: { label: "Koncept", cls: "bg-gray-100 text-gray-500", icon: FileText },
        cancelled: { label: "Stornováno", cls: "bg-red-100 text-red-700", icon: XCircle },
    };

    const renderInvoice = (inv) => {
        const st = INV_STATUS[inv.status] || INV_STATUS.sent;
        const StIcon = st.icon;
        const isInvoice = !!inv.invoice_number;

        return (
            <div key={inv.id || inv.invoice_number} className="flex items-center gap-4 bg-white rounded-xl border p-4 hover:shadow-sm transition-shadow"
                style={{ borderColor: "var(--sg-border)" }} data-testid={`invoice-card-${inv.id}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${st.cls}`}>
                    <StIcon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-mono text-sm font-bold text-gray-800">{inv.invoice_number || inv.order_number}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span>{inv.issue_date || new Date(inv.created_at).toLocaleDateString("cs-CZ")}</span>
                        {inv.due_date && <span>Splatnost: <span className="font-medium">{inv.due_date}</span></span>}
                        {inv.customer_name && <span>{inv.customer_name}</span>}
                    </div>
                </div>
                <div className="text-right flex-shrink-0">
                    <p className="text-base font-bold text-gray-900">{fmt(inv.total || inv.total_amount)} Kč</p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                    {isInvoice ? (
                        <>
                            <a href={`${backendUrl}/api/invoices/${inv.id}/pdf?token=${token}`} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border hover:bg-gray-50 transition-colors" style={{ color: "var(--sg-primary)", borderColor: "var(--sg-border)" }}
                                data-testid={`pdf-invoice-${inv.id}`}>
                                <Download size={12} /> PDF
                            </a>
                            <a href={`${backendUrl}/api/invoices/${inv.id}/isdoc?token=${token}`} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
                                data-testid={`isdoc-invoice-${inv.id}`}>
                                <FileCode size={12} /> ISDOC
                            </a>
                        </>
                    ) : (
                        <>
                            <a href={`${backendUrl}/api/customer/orders/${inv.id}/invoice-pdf?token=${token}`} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border hover:bg-gray-50 transition-colors" style={{ color: "var(--sg-primary)", borderColor: "var(--sg-border)" }}>
                                <Download size={12} /> PDF
                            </a>
                            <a href={`${backendUrl}/api/customer/orders/${inv.id}/invoice-isdoc?token=${token}`} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
                                data-testid={`isdoc-order-${inv.id}`}>
                                <FileCode size={12} /> ISDOC
                            </a>
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4" data-testid="invoices-tab">
            <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-bold" style={{ color: "var(--sg-text)" }}>Faktury</h2>
                <span className="text-xs text-gray-400">{invoices.length} faktur</span>
            </div>

            {/* Summary cards */}
            {invoices.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded-xl border p-3 text-center" style={{ borderColor: "var(--sg-border)" }}>
                        <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Celkem</p>
                        <p className="text-lg font-bold text-gray-900">{invoices.length}</p>
                    </div>
                    <div className="bg-white rounded-xl border p-3 text-center" style={{ borderColor: "var(--sg-border)" }}>
                        <p className="text-[10px] text-amber-500 uppercase font-bold mb-1">Nezaplaceno</p>
                        <p className="text-lg font-bold text-amber-600">{unpaid.length}</p>
                    </div>
                    <div className="bg-white rounded-xl border p-3 text-center" style={{ borderColor: "var(--sg-border)" }}>
                        <p className="text-[10px] text-green-500 uppercase font-bold mb-1">Zaplaceno</p>
                        <p className="text-lg font-bold text-green-600">{paid.length}</p>
                    </div>
                </div>
            )}

            {loading ? <Card><p className="text-sm text-gray-400 text-center py-6">Načítám...</p></Card> :
                invoices.length === 0 ? (
                    <Card>
                        <div className="text-center py-8">
                            <FileText size={28} className="mx-auto mb-2 text-gray-300" />
                            <p className="text-sm text-gray-400">Žádné faktury</p>
                        </div>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {unpaid.length > 0 && (
                            <div>
                                <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                    <Clock size={10} /> K úhradě ({unpaid.length})
                                </p>
                                <div className="space-y-2">
                                    {unpaid.map(renderInvoice)}
                                </div>
                            </div>
                        )}
                        {paid.length > 0 && (
                            <div>
                                <p className="text-[10px] font-bold text-green-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                    <CheckCircle size={10} /> Zaplaceno ({paid.length})
                                </p>
                                <div className="space-y-2">
                                    {paid.map(renderInvoice)}
                                </div>
                            </div>
                        )}
                    </div>
                )
            }
        </div>
    );
}

// ─── Shipping ──────────────────────────────────────────────────────────────

function ShippingTab() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/customer/orders").then(r => setOrders(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-3" data-testid="shipping-tab">
            <h2 className="text-sm font-bold" style={{ color: "var(--sg-text)" }}>Expedice a sledování zásilek</h2>
            {loading ? <Card><p className="text-sm text-gray-400 text-center py-6">Načítám...</p></Card> :
                orders.length === 0 ? <Card><p className="text-sm text-gray-400 text-center py-8">Žádné zásilky</p></Card> :
                orders.map(o => (
                    <Card key={o.id}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="font-mono text-sm font-bold">{o.order_number}</span>
                                <StatusBadge status={o.status} />
                            </div>
                            <span className="text-xs text-gray-400">{new Date(o.created_at).toLocaleDateString("cs-CZ")}</span>
                        </div>
                        {o.tracking_number ? (
                            <div className="mt-2 p-2.5 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm">
                                    <Truck size={14} className="text-green-600" />
                                    <span className="font-mono font-medium text-green-700">{o.tracking_number}</span>
                                </div>
                                <a href={`https://www.ppl.cz/detail-zasilky/${o.tracking_number}`} target="_blank" rel="noopener noreferrer"
                                    className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center gap-1">
                                    Sledovat <ChevronRight size={12} />
                                </a>
                            </div>
                        ) : (
                            <p className="mt-2 text-xs text-gray-400 flex items-center gap-1"><Clock size={11} /> Čeká na expedici</p>
                        )}
                    </Card>
                ))}
        </div>
    );
}

// ─── Inquiries ─────────────────────────────────────────────────────────────

function InquiriesTab() {
    const [inquiries, setInquiries] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ subject: "", description: "", budget: "" });
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        api.get("/customer/inquiries").then(r => setInquiries(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const handleSubmit = async () => {
        if (!form.subject) { toast.error("Vyplňte předmět"); return; }
        try {
            await api.post("/customer/inquiries", form);
            toast.success("Poptávka odeslána");
            setShowForm(false); setForm({ subject: "", description: "", budget: "" }); load();
        } catch (e) { toast.error("Chyba"); }
    };

    return (
        <div className="space-y-3" data-testid="inquiries-tab">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold" style={{ color: "var(--sg-text)" }}>Moje poptávky</h2>
                <Button size="sm" onClick={() => setShowForm(true)} className="text-white" style={{ backgroundColor: "var(--sg-primary)" }} data-testid="new-inquiry-btn">
                    <Plus size={12} className="mr-1" /> Nová poptávka
                </Button>
            </div>
            {showForm && (
                <Card>
                    <div className="space-y-3">
                        <div><Label className="text-xs text-gray-600 mb-1">Předmět *</Label>
                            <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} className="h-9 text-sm" data-testid="inquiry-subject" /></div>
                        <div><Label className="text-xs text-gray-600 mb-1">Popis</Label>
                            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                className="w-full h-20 px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: "var(--sg-border)" }} /></div>
                        <div><Label className="text-xs text-gray-600 mb-1">Rozpočet</Label>
                            <Input value={form.budget} onChange={e => setForm(p => ({ ...p, budget: e.target.value }))} placeholder="cca 150 000 Kč" className="h-9 text-sm" /></div>
                        <div className="flex gap-2">
                            <Button size="sm" onClick={handleSubmit} className="text-white" style={{ backgroundColor: "var(--sg-primary)" }}>Odeslat</Button>
                            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Zrušit</Button>
                        </div>
                    </div>
                </Card>
            )}
            {inquiries.map(i => (
                <Card key={i.id}>
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{i.subject}</span>
                        <StatusBadge status={i.status} />
                    </div>
                    {i.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{i.description}</p>}
                    <span className="text-[10px] text-gray-400 mt-1 block">{new Date(i.created_at).toLocaleDateString("cs-CZ")}</span>
                </Card>
            ))}
            {!loading && inquiries.length === 0 && !showForm && <Card><p className="text-sm text-gray-400 text-center py-6">Žádné poptávky</p></Card>}
        </div>
    );
}


// ─── Warranties (Záruky) ───────────────────────────────────────────────────

function WarrantiesTab() {
    const [warranties, setWarranties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [claimForm, setClaimForm] = useState(null); // warranty object or null
    const [sending, setSending] = useState(false);

    const load = useCallback(() => {
        api.get("/customer/warranties").then(r => setWarranties(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const handleSubmitClaim = async () => {
        if (!claimForm?.reason) { toast.error("Vyplnte duvod reklamace"); return; }
        setSending(true);
        try {
            await api.post("/customer/warranty-claim", {
                warranty_id: claimForm.id,
                reason: claimForm.reason,
                description: claimForm.description || "",
                resolution_requested: claimForm.resolution_requested || "repair",
            });
            toast.success("Reklamace odeslana");
            setClaimForm(null);
            load();
        } catch { toast.error("Chyba odeslani"); } finally { setSending(false); }
    };

    const fmtDate = (d) => d ? d.substring(0, 10) : "-";

    return (
        <div className="space-y-3" data-testid="warranties-tab">
            <h2 className="text-sm font-bold" style={{ color: "var(--sg-text)" }}>Moje zaruky</h2>
            {loading ? <p className="text-xs text-gray-400 py-4 text-center">Nacitam...</p> : warranties.length === 0 ? (
                <Card><p className="text-sm text-gray-400 text-center py-6">Zatim nemáte zadne zaruky.</p></Card>
            ) : warranties.map(w => (
                <Card key={w.id}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                    w.is_valid ? "bg-green-100 text-green-700" : "bg-red-50 text-red-500"
                                }`}>
                                    {w.is_valid ? <ShieldCheck size={10} /> : <ShieldX size={10} />}
                                    {w.is_valid ? "V zaruce" : "Po zaruce"}
                                </span>
                                <span className="text-[10px] text-gray-400 font-mono">{w.warranty_number}</span>
                            </div>
                            <p className="text-sm font-medium text-gray-800 truncate">{w.product_name}</p>
                            <div className="flex gap-4 mt-1 text-[11px] text-gray-500">
                                <span>Faktura: {w.invoice_number || "-"}</span>
                                <span>Zaruka: {w.warranty_months} mes.</span>
                                <span>Vystaveno: {fmtDate(w.invoice_date)}</span>
                                <span>Expiruje: {fmtDate(w.warranty_expires)}</span>
                                {w.is_valid && <span className="text-green-600 font-medium">Zbyvá {w.days_remaining} dní</span>}
                            </div>
                        </div>
                        {w.is_valid && (
                            <Button size="sm" variant="outline" onClick={() => setClaimForm({ ...w, reason: "", description: "", resolution_requested: "repair" })}
                                className="text-orange-600 border-orange-200 hover:bg-orange-50 text-xs flex-shrink-0" data-testid={`claim-warranty-${w.id}`}>
                                <AlertTriangle size={11} className="mr-1" /> Reklamovat
                            </Button>
                        )}
                    </div>
                </Card>
            ))}

            {/* Claim form modal */}
            {claimForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="warranty-claim-modal">
                    <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-gray-900">Reklamace: {claimForm.product_name}</h3>
                            <button onClick={() => setClaimForm(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={16} /></button>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                            <ShieldCheck size={14} className="text-green-600" />
                            <span className="text-xs text-green-700 font-medium">
                                Zaruka #{claimForm.warranty_number} – platna do {fmtDate(claimForm.warranty_expires)} ({claimForm.days_remaining} dni)
                            </span>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs text-gray-600 mb-1">Duvod reklamace *</Label>
                                <Input value={claimForm.reason} onChange={e => setClaimForm(f => ({ ...f, reason: e.target.value }))}
                                    placeholder="Popis zavady..." className="text-sm" data-testid="warranty-claim-reason" />
                            </div>
                            <div>
                                <Label className="text-xs text-gray-600 mb-1">Podrobny popis</Label>
                                <textarea value={claimForm.description} onChange={e => setClaimForm(f => ({ ...f, description: e.target.value }))}
                                    className="w-full h-20 px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: "var(--sg-border)" }}
                                    placeholder="Kdy se zavada projevila, jak se chova..." />
                            </div>
                            <div>
                                <Label className="text-xs text-gray-600 mb-1">Pozadovane reseni</Label>
                                <select value={claimForm.resolution_requested} onChange={e => setClaimForm(f => ({ ...f, resolution_requested: e.target.value }))}
                                    className="w-full h-9 px-3 rounded-md border text-sm" style={{ borderColor: "var(--sg-border)" }}>
                                    <option value="repair">Oprava</option>
                                    <option value="replace">Vymena</option>
                                    <option value="refund">Vraceni penez</option>
                                    <option value="credit">Dobropis</option>
                                </select>
                            </div>
                            <div className="flex gap-2 pt-1">
                                <Button onClick={handleSubmitClaim} disabled={sending} className="flex-1 text-white" style={{ backgroundColor: "var(--sg-primary)" }}
                                    data-testid="warranty-claim-submit">
                                    {sending ? "Odesilam..." : "Odeslat reklamaci"}
                                </Button>
                                <Button variant="outline" onClick={() => setClaimForm(null)} className="flex-1">Zrusit</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


// ─── Complaints ────────────────────────────────────────────────────────────

function ComplaintsTab() {
    const [complaints, setComplaints] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ order_number: "", product_name: "", reason: "", description: "" });
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        api.get("/customer/complaints").then(r => setComplaints(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const handleSubmit = async () => {
        if (!form.reason) { toast.error("Vyplňte důvod reklamace"); return; }
        try {
            await api.post("/customer/complaints", form);
            toast.success("Reklamace odeslána");
            setShowForm(false); setForm({ order_number: "", product_name: "", reason: "", description: "" }); load();
        } catch (e) { toast.error("Chyba"); }
    };

    return (
        <div className="space-y-3" data-testid="complaints-tab">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold" style={{ color: "var(--sg-text)" }}>Reklamace</h2>
                <Button size="sm" onClick={() => setShowForm(true)} className="text-white" style={{ backgroundColor: "var(--sg-primary)" }} data-testid="new-complaint-btn">
                    <Plus size={12} className="mr-1" /> Nová reklamace
                </Button>
            </div>
            {showForm && (
                <Card>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs text-gray-600 mb-1">Číslo objednávky</Label>
                                <Input value={form.order_number} onChange={e => setForm(p => ({ ...p, order_number: e.target.value }))} className="h-9 text-sm font-mono" /></div>
                            <div><Label className="text-xs text-gray-600 mb-1">Produkt</Label>
                                <Input value={form.product_name} onChange={e => setForm(p => ({ ...p, product_name: e.target.value }))} className="h-9 text-sm" /></div>
                        </div>
                        <div><Label className="text-xs text-gray-600 mb-1">Důvod reklamace *</Label>
                            <Input value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} className="h-9 text-sm" data-testid="complaint-reason" /></div>
                        <div><Label className="text-xs text-gray-600 mb-1">Podrobný popis</Label>
                            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                className="w-full h-20 px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: "var(--sg-border)" }} /></div>
                        <div className="flex gap-2">
                            <Button size="sm" onClick={handleSubmit} className="text-white" style={{ backgroundColor: "var(--sg-primary)" }}>Odeslat</Button>
                            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Zrušit</Button>
                        </div>
                    </div>
                </Card>
            )}
            {complaints.map(c => (
                <Card key={c.id}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {c.claim_number && <span className="text-xs font-mono text-gray-500">{c.claim_number}</span>}
                            <span className="text-sm font-medium">{c.reason}</span>
                        </div>
                        <StatusBadge status={c.status} />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                        {c.order_number && <span className="font-mono mr-2">{c.order_number}</span>}
                        {c.product_name && <span>{c.product_name}</span>}
                        {c.items && c.items.length > 0 && !c.product_name && <span>{c.items.map(i => i.name).join(', ')}</span>}
                    </div>
                    {c.resolution && <p className="text-xs text-green-600 mt-1">Řešení: {c.resolution}</p>}
                    {c.admin_notes && <p className="text-xs text-blue-600 mt-1">Poznámka: {c.admin_notes}</p>}
                    <span className="text-[10px] text-gray-400 mt-1 block">{new Date(c.created_at).toLocaleDateString("cs-CZ")}</span>
                </Card>
            ))}
            {!loading && complaints.length === 0 && !showForm && <Card><p className="text-sm text-gray-400 text-center py-6">Žádné reklamace</p></Card>}
        </div>
    );
}

// ─── Tickets ───────────────────────────────────────────────────────────────

function TicketsTab() {
    const [tickets, setTickets] = useState([]);
    const [activeTicket, setActiveTicket] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ subject: "", message: "", ticket_type: "general" });
    const [reply, setReply] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        api.get("/customer/tickets").then(r => setTickets(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const handleCreate = async () => {
        if (!form.subject || !form.message) { toast.error("Vyplňte předmět a zprávu"); return; }
        try {
            await api.post("/customer/tickets", form);
            toast.success("Zpráva odeslána");
            setShowForm(false); setForm({ subject: "", message: "", ticket_type: "general" }); load();
        } catch (e) { toast.error("Chyba"); }
    };

    const handleReply = async () => {
        if (!reply.trim()) return;
        try {
            await api.post(`/customer/tickets/${activeTicket.id}/reply`, { message: reply });
            setReply("");
            const updated = await api.get("/customer/tickets");
            setTickets(updated.data || []);
            const fresh = (updated.data || []).find(t => t.id === activeTicket.id);
            if (fresh) setActiveTicket(fresh);
            toast.success("Odpověď odeslána");
        } catch (e) { toast.error("Chyba"); }
    };

    if (activeTicket) {
        return (
            <div className="space-y-3" data-testid="ticket-detail">
                <button onClick={() => setActiveTicket(null)} className="text-xs text-gray-500 hover:text-green-700 flex items-center gap-1">
                    &larr; Zpět na seznam
                </button>
                <Card>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-sm">{activeTicket.subject}</h3>
                        <StatusBadge status={activeTicket.status} />
                    </div>
                    <div className="space-y-3 max-h-96 overflow-y-auto sg-scrollbar">
                        {(activeTicket.messages || []).map((m, i) => (
                            <div key={i} className={`p-3 rounded-lg text-sm ${m.sender === "admin" ? "bg-blue-50 border border-blue-200 ml-6" : "bg-gray-50 border border-gray-200 mr-6"}`}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="font-medium text-xs">{m.sender === "admin" ? "Solar-Green" : m.name || "Vy"}</span>
                                    <span className="text-[10px] text-gray-400">{new Date(m.at).toLocaleString("cs-CZ")}</span>
                                </div>
                                <p className="text-gray-700">{m.text}</p>
                            </div>
                        ))}
                    </div>
                    {activeTicket.status !== "closed" && (
                        <div className="flex gap-2 mt-3">
                            <Input value={reply} onChange={e => setReply(e.target.value)} placeholder="Napište odpověď..."
                                className="h-9 text-sm flex-1" onKeyDown={e => e.key === "Enter" && handleReply()} data-testid="ticket-reply-input" />
                            <Button size="sm" onClick={handleReply} className="text-white" style={{ backgroundColor: "var(--sg-primary)" }} data-testid="ticket-reply-btn">
                                <Send size={12} className="mr-1" /> Odeslat
                            </Button>
                        </div>
                    )}
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="tickets-tab">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold" style={{ color: "var(--sg-text)" }}>Komunikace</h2>
                <Button size="sm" onClick={() => setShowForm(true)} className="text-white" style={{ backgroundColor: "var(--sg-primary)" }} data-testid="new-ticket-btn">
                    <Plus size={12} className="mr-1" /> Nová zpráva
                </Button>
            </div>
            {showForm && (
                <Card>
                    <div className="space-y-3">
                        <div><Label className="text-xs text-gray-600 mb-1">Předmět *</Label>
                            <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} className="h-9 text-sm" data-testid="ticket-subject" /></div>
                        <div><Label className="text-xs text-gray-600 mb-1">Typ</Label>
                            <select value={form.ticket_type} onChange={e => setForm(p => ({ ...p, ticket_type: e.target.value }))}
                                className="w-full h-9 px-3 border rounded-md text-sm" style={{ borderColor: "var(--sg-border)" }}>
                                <option value="general">Obecný dotaz</option>
                                <option value="order">K objednávce</option>
                                <option value="technical">Technický</option>
                            </select></div>
                        <div><Label className="text-xs text-gray-600 mb-1">Zpráva *</Label>
                            <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                                className="w-full h-24 px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: "var(--sg-border)" }} data-testid="ticket-message" /></div>
                        <div className="flex gap-2">
                            <Button size="sm" onClick={handleCreate} className="text-white" style={{ backgroundColor: "var(--sg-primary)" }}>Odeslat</Button>
                            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Zrušit</Button>
                        </div>
                    </div>
                </Card>
            )}
            {tickets.map(t => (
                <Card key={t.id} className="cursor-pointer hover:shadow-sm transition-shadow" onClick={() => setActiveTicket(t)}>
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{t.subject}</span>
                        <div className="flex items-center gap-2">
                            <StatusBadge status={t.status} />
                            <ChevronRight size={14} className="text-gray-300" />
                        </div>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                        {(t.messages || []).length} zpráv | {new Date(t.updated_at).toLocaleDateString("cs-CZ")}
                    </div>
                </Card>
            ))}
            {!loading && tickets.length === 0 && !showForm && <Card><p className="text-sm text-gray-400 text-center py-6">Žádné zprávy</p></Card>}
        </div>
    );
}

// ─── Addresses ─────────────────────────────────────────────────────────────

function AddressesTab() {
    const [addresses, setAddresses] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ label: "Domů", name: "", street: "", city: "", zip: "", phone: "", is_default: false });
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        api.get("/customer/addresses").then(r => setAddresses(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const handleSave = async () => {
        if (!form.name || !form.street || !form.city || !form.zip) { toast.error("Vyplňte povinná pole"); return; }
        try {
            await api.post("/customer/addresses", form);
            toast.success("Adresa přidána");
            setShowForm(false); setForm({ label: "Domů", name: "", street: "", city: "", zip: "", phone: "", is_default: false }); load();
        } catch (e) { toast.error("Chyba"); }
    };

    const handleDelete = async (id) => {
        await api.delete(`/customer/addresses/${id}`);
        toast.success("Adresa smazána"); load();
    };

    return (
        <div className="space-y-3" data-testid="addresses-tab">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold" style={{ color: "var(--sg-text)" }}>Dodací adresy</h2>
                <Button size="sm" onClick={() => setShowForm(true)} className="text-white" style={{ backgroundColor: "var(--sg-primary)" }} data-testid="new-address-btn">
                    <Plus size={12} className="mr-1" /> Nová adresa
                </Button>
            </div>
            {showForm && (
                <Card>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs text-gray-600 mb-1">Označení</Label>
                                <Input value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="Domů / Práce" className="h-9 text-sm" /></div>
                            <div><Label className="text-xs text-gray-600 mb-1">Jméno *</Label>
                                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="h-9 text-sm" data-testid="addr-name" /></div>
                        </div>
                        <div><Label className="text-xs text-gray-600 mb-1">Ulice *</Label>
                            <Input value={form.street} onChange={e => setForm(p => ({ ...p, street: e.target.value }))} className="h-9 text-sm" /></div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2"><Label className="text-xs text-gray-600 mb-1">Město *</Label>
                                <Input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} className="h-9 text-sm" /></div>
                            <div><Label className="text-xs text-gray-600 mb-1">PSČ *</Label>
                                <Input value={form.zip} onChange={e => setForm(p => ({ ...p, zip: e.target.value }))} className="h-9 text-sm" /></div>
                        </div>
                        <div><Label className="text-xs text-gray-600 mb-1">Telefon</Label>
                            <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="h-9 text-sm" /></div>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={form.is_default} onChange={e => setForm(p => ({ ...p, is_default: e.target.checked }))} className="w-4 h-4 accent-green-600" />
                            Výchozí adresa
                        </label>
                        <div className="flex gap-2">
                            <Button size="sm" onClick={handleSave} className="text-white" style={{ backgroundColor: "var(--sg-primary)" }}>Uložit</Button>
                            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Zrušit</Button>
                        </div>
                    </div>
                </Card>
            )}
            {addresses.map(a => (
                <Card key={a.id} className="flex items-start justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <MapPin size={13} style={{ color: "var(--sg-primary)" }} />
                            <span className="font-medium text-sm">{a.label}</span>
                            {a.is_default && <Badge variant="secondary" className="text-[10px]">Výchozí</Badge>}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{a.name}, {a.street}, {a.city} {a.zip}</p>
                    </div>
                    <button onClick={() => handleDelete(a.id)} className="text-xs text-red-400 hover:text-red-600">Smazat</button>
                </Card>
            ))}
            {!loading && addresses.length === 0 && !showForm && <Card><p className="text-sm text-gray-400 text-center py-6">Žádné uložené adresy</p></Card>}
        </div>
    );
}

// ─── Settings ──────────────────────────────────────────────────────────────

function SettingsTab() {
    const { customer } = useCustomerAuth();
    const [profile, setProfile] = useState({ name: customer?.name || "", phone: customer?.phone || "", company: customer?.company || "", ico: customer?.ico || "", dic: customer?.dic || "" });
    const [pw, setPw] = useState({ old_password: "", new_password: "" });
    const [saving, setSaving] = useState(false);

    const handleProfile = async () => {
        setSaving(true);
        try {
            await api.put("/customer/profile", profile);
            toast.success("Profil aktualizován");
        } catch (e) { toast.error(e.response?.data?.detail || "Chyba"); }
        finally { setSaving(false); }
    };

    const handlePassword = async () => {
        if (pw.new_password.length < 6) { toast.error("Nové heslo min. 6 znaků"); return; }
        try {
            await api.put("/customer/password", pw);
            toast.success("Heslo změněno");
            setPw({ old_password: "", new_password: "" });
        } catch (e) { toast.error(e.response?.data?.detail || "Chyba"); }
    };

    return (
        <div className="space-y-4" data-testid="settings-tab">
            <h2 className="text-sm font-bold" style={{ color: "var(--sg-text)" }}>Nastavení účtu</h2>
            <Card>
                <h3 className="text-sm font-semibold mb-3">Osobní údaje</h3>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs text-gray-600 mb-1">Jméno</Label>
                            <Input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} className="h-9 text-sm" data-testid="profile-name" /></div>
                        <div><Label className="text-xs text-gray-600 mb-1">Telefon</Label>
                            <Input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} className="h-9 text-sm" /></div>
                    </div>
                    <div><Label className="text-xs text-gray-600 mb-1">Firma</Label>
                        <Input value={profile.company} onChange={e => setProfile(p => ({ ...p, company: e.target.value }))} className="h-9 text-sm" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs text-gray-600 mb-1">IČO</Label>
                            <Input value={profile.ico} onChange={e => setProfile(p => ({ ...p, ico: e.target.value }))} className="h-9 text-sm" /></div>
                        <div><Label className="text-xs text-gray-600 mb-1">DIČ</Label>
                            <Input value={profile.dic} onChange={e => setProfile(p => ({ ...p, dic: e.target.value }))} className="h-9 text-sm" /></div>
                    </div>
                    <Button size="sm" onClick={handleProfile} disabled={saving} className="text-white" style={{ backgroundColor: "var(--sg-primary)" }} data-testid="save-profile-btn">
                        {saving ? "Ukládám..." : "Uložit profil"}
                    </Button>
                </div>
            </Card>
            <Card>
                <h3 className="text-sm font-semibold mb-3">Změna hesla</h3>
                <div className="space-y-3">
                    <div><Label className="text-xs text-gray-600 mb-1">Současné heslo</Label>
                        <Input type="password" value={pw.old_password} onChange={e => setPw(p => ({ ...p, old_password: e.target.value }))} className="h-9 text-sm" /></div>
                    <div><Label className="text-xs text-gray-600 mb-1">Nové heslo</Label>
                        <Input type="password" value={pw.new_password} onChange={e => setPw(p => ({ ...p, new_password: e.target.value }))} className="h-9 text-sm" /></div>
                    <Button size="sm" onClick={handlePassword} variant="outline">Změnit heslo</Button>
                </div>
            </Card>
        </div>
    );
}
